import React, { useState } from 'react';
import {
  X, Instagram, Facebook, Linkedin, Youtube,
  ShieldCheck, AlertCircle, Info, ChevronRight, Check,
  BookOpen, ExternalLink, Key
} from 'lucide-react';
import {
  Dialog, DialogContent, DialogHeader,
  DialogTitle, DialogDescription, DialogFooter
} from "@/ui/dialog";
import { Button } from '@/ui/button';
import { Input } from '@/ui/input';
import { Label } from '@/ui/label';
import API from '@/api/api01';
import { toast } from 'sonner';

interface ConnectIntegrationModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess: () => void;
}

const SETUP_GUIDES: Record<string, { title: string; steps: string[]; link: string }> = {
  Instagram: {
    title: 'Instagram API Setup',
    steps: [
      'Log into Meta for Developers portal.',
      'Create a new App (Type: Business).',
      'Add "Instagram Graph API" to your app.',
      'Go to "Tools" -> "Graph API Explorer".',
      'Select your App and generate a User Access Token.',
      'Exchange it for a "Long-Lived Token" in Settings.'
    ],
    link: 'https://developers.facebook.com/docs/instagram-api'
  },
  Facebook: {
    title: 'Facebook Lead Ads Setup',
    steps: [
      'Visit Meta Business Suite Account Settings.',
      'Navigate to "Ad Accounts" and copy your ID.',
      'Go to "Security Center" and enable 2FA.',
      'In Developers Portal, App -> Dashboad -> Marketing API.',
      'Generate a Token with "ads_management" permissions.'
    ],
    link: 'https://www.facebook.com/business/help/397336587121938'
  },
  LinkedIn: {
    title: 'LinkedIn Ads Integration',
    steps: [
      'Open LinkedIn Campaign Manager.',
      'Select your Account -> Account Settings.',
      'Go to "Lead Gen Forms" -> "API Keys".',
      'Create a new API Key with "Lead Retrieval" access.',
      'Copy the Client ID and Secret Provided.'
    ],
    link: 'https://business.linkedin.com/marketing-solutions/ads/lead-gen-forms'
  },
  YouTube: {
    title: 'YouTube Google Ads Setup',
    steps: [
      'Sign in to your Google Ads account.',
      'Go to "Tools & Settings" -> "API Center".',
      'Request a "Developer Token" from Google.',
      'In Google Cloud Console, enable "Google Ads API".',
      'Generate an OAuth2 Client ID and Secret.'
    ],
    link: 'https://developers.google.com/google-ads/api/docs/start'
  }
};

export default function ConnectIntegrationModal({ open, onOpenChange, onSuccess }: ConnectIntegrationModalProps) {
  const [loading, setLoading] = useState(false);
  const [step, setStep] = useState(1);
  const [showGuide, setShowGuide] = useState(false);
  const [platform, setPlatform] = useState('');
  const [accountName, setAccountName] = useState('');
  const [accountId, setAccountId] = useState('');
  const [accessToken, setAccessToken] = useState('');

  const PLATFORM_OPTIONS = [
    { id: 'Instagram', name: 'Instagram', icon: <Instagram className="text-[#E1306C]" />, desc: 'Ads & Leads' },
    { id: 'Facebook', name: 'Facebook', icon: <Facebook className="text-[#1877F2]" />, desc: 'Lead Forms' },
    { id: 'LinkedIn', name: 'LinkedIn', icon: <Linkedin className="text-[#0A66C2]" />, desc: 'Ads Manager' },
    { id: 'YouTube', name: 'YouTube', icon: <Youtube className="text-[#FF0000]" />, desc: 'Extensions' },
  ];

  const handleConnect = async () => {
    if (!accountName) {
      toast.error("Please provide an account name");
      return;
    }

    try {
      setLoading(true);
      await API.post('/superadmin/social-integrations', {
        platform,
        account_name: accountName,
        account_id: accountId,
        access_token: accessToken,
        status: 'Connected'
      });
      toast.success(`${platform} connected successfully!`);
      onSuccess();
      onOpenChange(false);
      resetForm();
    } catch (err) {
      toast.error("Failed to connect account.");
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setPlatform('');
    setAccountName('');
    setAccountId('');
    setAccessToken('');
    setStep(1);
    setShowGuide(false);
  };

  const currentGuide = SETUP_GUIDES[platform];

  return (
    <Dialog open={open} onOpenChange={(v) => { onOpenChange(v); if (!v) resetForm(); }}>
      <DialogContent className="sm:max-w-[500px] w-[95vw] rounded-[2rem] border-none shadow-2xl p-0 overflow-hidden bg-white dark:bg-slate-950 max-h-[95vh] flex flex-col [&>button]:text-white [&>button]:bg-white/10 [&>button]:hover:bg-white/20 [&>button]:p-2 [&>button]:rounded-full [&>button]:top-6 [&>button]:right-6 [&>button]:opacity-100 [&>button]:transition-all [&>button]:z-[60]">
        {/* Animated Header */}
        <div className="bg-slate-900 p-6 sm:p-8 text-white relative overflow-hidden shrink-0 transition-all duration-500">
          <div className="absolute top-0 left-0 w-full h-full opacity-10 pointer-events-none">
            <div className="absolute top-[-50%] left-[-20%] w-[140%] h-[140%] bg-[radial-gradient(circle,rgba(124,58,237,0.4)_0%,transparent_70%)]" />
            <div className="absolute w-full h-full bg-[url('https://www.transparenttextures.com/patterns/carbon-fibre.png')] opacity-20" />
          </div>

          <DialogHeader className="relative z-10">
            <div className="flex items-center justify-between mb-4 pr-12">
              <div className="h-10 w-10 sm:h-12 sm:w-12 rounded-2xl bg-white/10 backdrop-blur-md flex items-center justify-center border border-white/20">
                {showGuide ? <BookOpen size={24} className="text-amber-400" /> : <ShieldCheck size={24} className="text-purple-400" />}
              </div>
              <div className="flex items-center gap-1.5 px-3 py-1 rounded-full bg-white/10 backdrop-blur-md border border-white/10 text-[9px] font-bold uppercase tracking-widest text-white/70">
                {showGuide ? 'Documentation' : `Step ${step} of 2`}
              </div>
            </div>
            <DialogTitle className="text-lg sm:text-xl font-bold tracking-tight leading-tight">
              {showGuide ? `${platform} Setup Guide` : (step === 1 ? 'Select Platform' : `Setup ${platform}`)}
            </DialogTitle>
            <DialogDescription className="text-white/50 mt-1 font-medium text-xs sm:text-sm leading-relaxed max-w-sm">
              {showGuide ? 'Follow these steps to obtain your API credentials.' : (step === 1 ? 'Choose the social network to sync' : 'Link your business account credentials.')}
            </DialogDescription>
          </DialogHeader>

        </div>

        {/* Scrollable Content Area */}
        <div className="flex-1 overflow-y-auto p-6 sm:p-8 bg-slate-50/50 dark:bg-slate-900/10 custom-scrollbar">
          {showGuide ? (
            <div className="space-y-6 animate-in fade-in zoom-in-95 duration-300">
              <div className="space-y-4">
                {currentGuide.steps.map((s, i) => (
                  <div key={i} className="flex gap-4 group">
                    <div className="h-6 w-6 rounded-full bg-slate-200 dark:bg-slate-800 flex items-center justify-center text-[10px] font-bold pointer-events-none group-hover:bg-amber-400 group-hover:text-amber-900 transition-colors shrink-0">
                      {i + 1}
                    </div>
                    <p className="text-sm font-semibold text-slate-600 dark:text-slate-400 leading-relaxed pt-0.5">
                      {s}
                    </p>
                  </div>
                ))}
              </div>
              <a
                href={currentGuide.link}
                target="_blank"
                rel="noreferrer"
                className="flex items-center justify-center gap-2 p-4 rounded-2xl bg-white dark:bg-slate-900 border-2 border-slate-100 dark:border-slate-800 hover:border-amber-400 transition-all text-sm font-bold text-slate-600 dark:text-slate-300"
              >
                Official Documentation <ExternalLink size={14} />
              </a>
            </div>
          ) : (
            <>
              {step === 1 ? (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  {PLATFORM_OPTIONS.map((opt) => (
                    <div
                      key={opt.id}
                      onClick={() => { setPlatform(opt.id); setStep(2); }}
                      className="group relative p-6 rounded-[1.5rem] border-2 border-transparent bg-white dark:bg-slate-900 shadow-sm hover:shadow-xl hover:border-purple-500 hover:-translate-y-1 transition-all cursor-pointer overflow-hidden"
                    >
                      <div className="absolute top-4 right-4 h-5 w-5 rounded-full bg-purple-500 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                        <ChevronRight size={12} className="text-white" />
                      </div>
                      <div className="h-10 w-10 rounded-xl bg-slate-50 dark:bg-slate-800 flex items-center justify-center mb-4 group-hover:scale-110 transition-transform">
                        {React.cloneElement(opt.icon as React.ReactElement, { size: 24 })}
                      </div>
                      <h4 className="font-bold text-slate-900 dark:text-white text-base">{opt.name}</h4>
                      <p className="text-[10px] text-slate-400 font-bold uppercase tracking-wider mt-0.5">{opt.desc}</p>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="space-y-6 animate-in slide-in-from-right-4 duration-500">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label className="text-[10px] font-bold uppercase tracking-widest text-slate-400 ml-1">Account Display Name</Label>
                      <Input
                        value={accountName}
                        onChange={(e) => setAccountName(e.target.value)}
                        placeholder="Social Agency"
                        className="h-12 rounded-xl border-none bg-white dark:bg-slate-900 shadow-sm focus:ring-2 focus:ring-purple-500 font-semibold text-sm"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label className="text-[10px] font-bold uppercase tracking-widest text-slate-400 ml-1">Business ID</Label>
                      <Input
                        value={accountId}
                        onChange={(e) => setAccountId(e.target.value)}
                        placeholder="Optional"
                        className="h-12 rounded-xl border-none bg-white dark:bg-slate-900 shadow-sm focus:ring-2 focus:ring-purple-500 font-semibold text-sm"
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <div className="flex items-center justify-between ml-1">
                      <Label className="text-[10px] font-bold uppercase tracking-widest text-slate-400">Access Token / API Key</Label>
                      <button
                        onClick={() => setShowGuide(true)}
                        className="text-[10px] text-amber-600 font-bold hover:underline flex items-center gap-1 group"
                      >
                        How to obtain? <Info size={10} className="group-hover:rotate-12 transition-transform" />
                      </button>
                    </div>
                    <Input
                      type="password"
                      value={accessToken}
                      onChange={(e) => setAccessToken(e.target.value)}
                      placeholder="Paste token here"
                      className="h-12 rounded-xl border-none bg-white dark:bg-slate-900 shadow-sm focus:ring-2 focus:ring-purple-500 font-semibold text-sm"
                    />
                  </div>

                  <div className="p-4 sm:p-5 bg-purple-600 rounded-[1.5rem] text-white flex gap-4 items-center relative overflow-hidden group shrink-0">
                    <div className="absolute top-0 left-0 w-full h-full bg-[radial-gradient(circle_at_center,rgba(255,255,255,0.1),transparent)]" />
                    <div className="h-10 w-10 rounded-xl bg-white/20 backdrop-blur-md flex items-center justify-center shrink-0 border border-white/20">
                      <Check size={20} className="text-white" />
                    </div>
                    <p className="text-[11px] text-purple-50 leading-relaxed font-bold z-10">
                      Webhooks will push leads to your board instantly.
                    </p>
                  </div>
                </div>
              )}
            </>
          )}
        </div>

        {/* Footer */}
        <DialogFooter className="p-6 sm:p-8 pt-0 flex flex-row items-center justify-between gap-4 shrink-0 bg-slate-50/50 dark:bg-slate-900/10">
          {showGuide ? (
            <Button variant="outline" onClick={() => setShowGuide(false)} className="rounded-2xl h-12 px-8 font-bold text-slate-500 border-slate-200 text-xs uppercase tracking-widest">
              Back to Setup
            </Button>
          ) : (
            <>
              {step === 2 && (
                <Button variant="outline" onClick={() => setStep(1)} className="rounded-2xl h-12 px-4 sm:px-8 font-bold text-slate-500 border-slate-200 text-sm">
                  Back
                </Button>
              )}
              <div className="flex-1" />
              <Button
                onClick={step === 2 ? handleConnect : undefined}
                disabled={loading || step === 1}
                className={`rounded-2xl h-12 px-8 font-bold shadow-xl transition-all text-sm ${step === 2 ? 'bg-purple-600 hover:bg-purple-700 shadow-purple-200' : 'bg-slate-200 text-slate-400 cursor-not-allowed'}`}
              >
                {loading ? 'Wait...' : 'Connect'}
              </Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
